import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ArrowRight, Phone, Video, Globe, Clock, CheckCircle2, ShieldCheck, MapPin,
  Search, DollarSign, HelpCircle, AlertTriangle, MessageSquare, Wrench, ChevronRight, Car,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/a458f44e-02cd-4e55-83d8-3e9684068794/images/270da228101a1ac37441a1be4171987c77b4e2b41645808ea5a7f56aa3948761.jpeg';
const PHONE = '321.300.3256';

const REVIEW = [
  { icon: Search, t: 'Vehicle Listing & Photos', d: 'We go through the listing and photos together, line by line.' },
  { icon: AlertTriangle, t: 'History & Red Flags', d: 'Vehicle history and the warning signs most buyers miss.' },
  { icon: DollarSign, t: 'Asking Price & Value', d: 'Whether the price is fair for the year, miles, and condition.' },
  { icon: HelpCircle, t: 'Questions To Ask the Seller', d: 'Exactly what to ask before you commit.' },
  { icon: Car, t: 'What To Watch On the Test Drive', d: 'The specific things to feel, hear, and look for.' },
  { icon: Wrench, t: 'Upcoming Costs & Concerns', d: 'Potential repairs or maintenance on the horizon.' },
  { icon: MessageSquare, t: 'Negotiation Opportunities', d: 'Where there may be room to negotiate a better deal.' },
  { icon: CheckCircle2, t: 'Recommended Next Steps', d: 'A clear plan: pursue it, pass on it, or inspect it in person.' },
];

const JOURNEY = [
  { n: '01', t: 'Find a Vehicle' },
  { n: '02', t: 'Book CarLens FirstLook™' },
  { n: '03', t: 'Review It With a CarLens Expert' },
  { n: '04', t: "Decide Whether It's Worth Pursuing" },
  { n: '05', t: 'Schedule a Physical Inspection if Needed' },
  { n: '06', t: 'Buy With Confidence' },
];

const FAQS = [
  { q: 'What is included in the consultation?', a: 'A private 30-minute video call with a CarLens vehicle expert. Before the call we review the listing, VIN, price, mileage, photos, and any history report you send. During the call we walk through the vehicle together — history and red flags, price and value, questions to ask the seller, what to watch on the test drive, potential upcoming costs, negotiation opportunities, and recommended next steps.' },
  { q: 'Is this a physical inspection?', a: 'No. FirstLook is expert guidance and a live consultation based on the information you provide — perfect before you drive out, negotiate, or send a deposit. If the vehicle is local to our Northeast Florida service area and passes FirstLook, you can upgrade to a full in-person CarLens Signature Inspection.' },
  { q: 'How long does it take?', a: 'The consultation is a focused 30 minutes. You choose the time that works for you after booking.' },
  { q: 'Do you come to me / is it virtual?', a: 'FirstLook is 100% virtual via Google Meet and available nationwide. No travel required.' },
  { q: 'Do I get my $49 back if I book a full inspection?', a: 'For customers within the CarLens local service area, your $49 FirstLook fee is credited toward a full CarLens Signature Inspection for that vehicle if you decide to proceed.' },
  { q: 'What should I send before the call?', a: 'The listing URL, VIN, year/make/model, mileage, asking price, seller type, any photos, and a Carfax/AutoCheck report if you have one — plus your biggest questions. We review it all before we meet.' },
];

const FirstLookLanding = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const book = () => navigate('/firstlook/start');

  const serviceSchema = {
    '@context': 'https://schema.org', '@type': 'Service',
    serviceType: '1-on-1 Car Buying Consultation',
    name: 'CarLens FirstLook™ — 1-on-1 Car Buying Consultation',
    description: 'A private 30-minute virtual car buying consultation with a CarLens vehicle expert. Review the listing, history, price, questions to ask, and next steps before you buy. Available nationwide.',
    provider: { '@type': 'AutomotiveBusiness', name: 'CarLens Inspections', telephone: '+13213003256', email: 'carlensauto@gmail.com', slogan: 'Know the Truth Before You Buy.' },
    areaServed: { '@type': 'Country', name: 'United States' },
    offers: { '@type': 'Offer', price: '49', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#0c0c0e' }} data-testid="firstlook-landing">
      <Seo siteName="CarLens" title="CarLens FirstLook™ | 1-on-1 Car Buying Consultation ($49)" description="CarLens FirstLook™ is a private 30-minute virtual car buying consultation with an expert — review the listing, history, price, and red flags before you buy. Intro offer $49, nationwide." path="/firstlook" image={HERO_IMG} faq={FAQS} extraSchemas={[serviceSchema]} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="CarLens expert reviewing a vehicle listing with a buyer before purchase" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(10,10,12,0.96) 0%, rgba(10,10,12,0.82) 45%, rgba(10,10,12,0.35) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,12,0.4) 0%, rgba(10,10,12,0) 30%, rgba(10,10,12,0.92) 100%)' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-orange-300 mb-6">
              CarLens FirstLook™ · 1-on-1 Car Buying Consultation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]" style={{ fontFamily: 'Space Grotesk' }} data-testid="firstlook-landing-h1">
              Before You Buy It, Let's Look At It Together.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-200 leading-snug">
              A private 30-minute virtual consultation with a CarLens vehicle expert — so you make a smarter decision before you purchase.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-300">
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> 30 Minutes</span>
              <span className="inline-flex items-center gap-2"><Video className="w-4 h-4 text-orange-400" /> Virtual</span>
              <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4 text-orange-400" /> Available Nationwide</span>
            </div>
            <div className="mt-7 inline-flex items-baseline gap-3 rounded-2xl border border-orange-500/40 bg-black/40 backdrop-blur px-6 py-3">
              <span className="text-xs uppercase tracking-widest text-orange-300 font-semibold">Introductory Offer</span>
              <span className="text-zinc-500 line-through text-xl">$79</span>
              <span className="text-4xl md:text-5xl font-extrabold text-orange-500" style={{ fontFamily: 'Space Grotesk' }}>$49</span>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={book} className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 h-auto text-base font-semibold shadow-lg shadow-orange-500/20" data-testid="firstlook-hero-cta">
                Book Your FirstLook — $49 <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a href={`tel:+13213003256`} className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-600 bg-white/5 backdrop-blur px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors" data-testid="firstlook-hero-call">
                <Phone className="w-4 h-4" /> Call Now · {PHONE}
              </a>
            </div>
            <p className="mt-4 text-sm text-zinc-400">Found a vehicle you're interested in? Send us the listing before your appointment and we'll look at it together.</p>
          </div>
        </div>
      </section>

      {/* WHAT WE REVIEW */}
      <Section eyebrow="During Your Consultation" title="A CarLens expert reviews the vehicle with you">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {REVIEW.map((c) => (
            <div key={c.t} className="rounded-2xl border border-zinc-800 bg-[#151517] p-6 hover:border-orange-500/40 transition-colors" data-testid={`firstlook-review-${c.t.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <c.icon className="w-7 h-7 text-orange-400 mb-3" />
              <h3 className="font-bold text-white leading-tight">{c.t}</h3>
              <p className="text-sm text-zinc-400 mt-1.5 leading-6">{c.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRICING */}
      <Section eyebrow="Introductory Offer" title="One flat price. Real expert guidance." dark>
        <div className="max-w-md mx-auto rounded-3xl border border-orange-500/40 bg-gradient-to-b from-[#1b1b1e] to-[#111113] p-8 text-center" data-testid="firstlook-pricing">
          <p className="text-sm uppercase tracking-widest text-orange-400 font-bold">CarLens FirstLook™</p>
          <div className="mt-4 flex items-end justify-center gap-3">
            <span className="text-zinc-500 line-through text-3xl mb-1">$79</span>
            <span className="text-6xl font-extrabold text-orange-500" style={{ fontFamily: 'Space Grotesk' }}>$49</span>
          </div>
          <div className="mt-6 space-y-2.5 text-left">
            {['30-Minute Private Video Consultation', 'Virtual', 'Available Nationwide', 'Pre-call vehicle review by a CarLens expert'].map((f) => (
              <div key={f} className="flex items-center gap-2.5 text-sm text-zinc-200"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0" /> {f}</div>
            ))}
          </div>
          <Button onClick={book} className="mt-7 w-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 h-auto text-base font-semibold" data-testid="firstlook-pricing-cta">
            Book Your FirstLook — $49 <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </Section>

      {/* JOURNEY */}
      <Section eyebrow="How FirstLook Works" title="Find it → Send it → We review it → Let's talk → You decide">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {JOURNEY.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-zinc-800 bg-gradient-to-b from-[#161618] to-[#111113] p-6" data-testid={`firstlook-journey-${s.n}`}>
              <span className="absolute top-5 right-5 text-sm font-extrabold text-zinc-700" style={{ fontFamily: 'Space Grotesk' }}>{s.n}</span>
              <h3 className="font-bold text-white text-lg pr-8">{s.t}</h3>
            </div>
          ))}
        </div>
      </Section>

      {/* LOCAL UPSELL / TWO CHOICES */}
      <Section eyebrow="Local To Northeast Florida?" title="FirstLook first — then inspect in person if it's worth it" dark>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-orange-500/40 bg-[#151517] p-8" data-testid="firstlook-choice-virtual">
            <Video className="w-8 h-8 text-orange-400 mb-3" />
            <h3 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>CarLens FirstLook™</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-7">Virtual expert guidance before deciding whether a vehicle is worth pursuing. $49, nationwide, 30 minutes.</p>
            <Button onClick={book} className="mt-5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 h-auto font-semibold" data-testid="firstlook-choice-virtual-cta">Book FirstLook — $49 <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="rounded-3xl border border-zinc-800 bg-[#151517] p-8" data-testid="firstlook-choice-inspection">
            <ShieldCheck className="w-8 h-8 text-orange-400 mb-3" />
            <h3 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>CarLens Signature Inspection</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-7">A comprehensive physical inspection for vehicles within the CarLens local service area. If your vehicle passes FirstLook, your <strong className="text-orange-300">$49 credits toward the inspection</strong>.</p>
            <Button onClick={() => navigate('/book')} variant="outline" className="mt-5 rounded-full border-zinc-600 text-white hover:bg-white/5 px-6 py-4 h-auto font-semibold" data-testid="firstlook-choice-inspection-cta">See Signature Inspection <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
        <div className="mt-8 rounded-2xl border border-zinc-800 bg-[#111113] p-6 text-center">
          <p className="text-lg text-white font-semibold" style={{ fontFamily: 'Space Grotesk' }}>"We do the homework before the call. Then we walk through it together."</p>
        </div>
      </Section>

      {/* FAQ */}
      <Section eyebrow="FAQ" title="Questions, answered">
        <div className="max-w-3xl mx-auto space-y-3" data-testid="firstlook-landing-faqs">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#151517] p-5" data-testid={`firstlook-landing-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white"><span>{f.q}</span><ChevronRight className="w-4 h-4 text-orange-400 shrink-0 transition-transform group-open:rotate-90" /></summary>
              <p className="mt-3 text-sm text-zinc-400 leading-7">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #26262b 0%, #17171a 50%, #0e0e10 100%)' }} />
        <div className="relative max-w-3xl mx-auto px-5 py-24 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">CarLens FirstLook™</p>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Talk to someone who knows what to look for.</h2>
          <p className="mt-4 text-lg text-zinc-300">30 Minutes · Virtual · Nationwide · Introductory Offer $49</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={book} className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 h-auto text-base font-semibold" data-testid="firstlook-final-cta">Book Your FirstLook <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <a href={`tel:+13213003256`} className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-600 bg-white/5 px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors" data-testid="firstlook-final-call"><Phone className="w-4 h-4" /> {PHONE}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

const Section = ({ eyebrow, title, children, dark }) => (
  <section className={dark ? 'bg-[#0e0e10]' : 'bg-[#0c0c0e]'}>
    <div className="max-w-6xl mx-auto px-5 py-20">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-widest text-orange-400 font-bold">{eyebrow}</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      </div>
      {children}
    </div>
  </section>
);

export default FirstLookLanding;
